/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-use-before-define */
import Container from '@brands/Components/Container/Container';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import PatientIntakeNavigation from '@brands/Components/PatientIntakeNavigation/PatientIntakeNavigation';
import RemoveItemModal from '@brands/Dashboard/Dashboard/Components/RemoveItemModal/RemoveItemModal';
import AddPharmacy from '@brands/Dashboard/Pharmacy/Components/AddPharmacy';
import SearchForPharmacy from '@brands/Dashboard/Pharmacy/Components/FindPharmacy';
import { PharmacySubmit } from '@brands/Dashboard/Pharmacy/Components/types';
import { useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useSnackbar } from '@brands/hooks/useStackbar';
import { CaseType } from '@brands/services/cases/types/ICase';
import { addClinicalData } from '@brands/services/clinical_data/addClinicalData';
import { listClinicalData } from '@brands/services/clinical_data/listClinicalData';
import { MedicalHistory } from '@brands/services/clinical_data/types/types';
import { updateClinicalData } from '@brands/services/clinical_data/updateClinicalData';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectStaffCaseCreation } from '@brands/store/selectors/staffCaseCreation';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { selectPatient } from '@brands/store/selectors/selectedPatient';
import GenericSnackbar from '@brands/Utils/GenericSnackbar';
import { isUnifiedStaffCaseIntakeRole } from '@brands/Utils/reviewProfileBasePath';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Button } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import styles from './styles.module.scss';

const CASE_TYPE_QUESTION_ID = 20;

const SelectPharmacy = (): JSX.Element => {
  const screenSize = useWindowSize();
  const { userInfo } = useAppSelector(selectAuth);
  const { selectedPatient: currentPatient } = useAppSelector(selectPatient);
  const { isStaffIntake } = useAppSelector(selectStaffCaseCreation);
  const isStaffPatientIntakeFlow =
    isStaffIntake && userInfo?.role?.name != null && isUnifiedStaffCaseIntakeRole(userInfo.role.name);
  const targetUserId =
    isStaffPatientIntakeFlow && currentPatient?.id ? Number(currentPatient.id) : Number(userInfo.id);
  const apiRole =
    isStaffPatientIntakeFlow && userInfo.role.name === UserRoleName.Collector
      ? UserRoleName.Collector
      : isStaffPatientIntakeFlow && userInfo.role.name === UserRoleName.CareAdvisor
        ? UserRoleName.CareAdvisor
        : undefined;
  const [render, setRender] = useState(true);
  const [pharmacyName, setPharmacyName] = useState('');
  const [pharmacyAddress, setPharmacyAddress] = useState('');
  const [findPharmacy, setFindPharmacy] = useState(false);
  const [addPharmacy, setAddPharmacy] = useState(false);
  const [objectId, setObjectId] = useState<string | null>(null);
  const [medicalHistory, setMedicalHistory] = useState<MedicalHistory | null>(null);
  const [removeItemModal, setRemoveItemModal] = useState(false);
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const { values } = useAppSelector(selectPatientForm);
  const roleNavigate = useRoleNavigate();
  const navigate = useNavigate();
  const AddNewPharmacy = async (newPharmacy: PharmacySubmit): Promise<void> => {
    try {
      if (!objectId) {
        await addClinicalData('medical_history', {
          user_id: targetUserId,
          under_care_of_physician: null,
          conditions_treated: null,
          taking_medication: null,
          medications: null,
          allergic_to_medications: null,
          medications_allergies: null,
          using_substances: [],
          interested_in_stopping: null,
          height: '',
          weight: '',
          pharmacy_name: newPharmacy.pharmacy_name,
          pharmacy_street_address: newPharmacy.pharmacy_street_address,
          pharmacy_city: newPharmacy.pharmacy_city,
          pharmacy_state: newPharmacy.pharmacy_state,
          pharmacy_zip_code: newPharmacy.pharmacy_zip_code,
          pharmacy_phone_number: newPharmacy.pharmacy_phone_number,
        }, apiRole);
      } else {
        await updateClinicalData('medical_history', objectId, {
          under_care_of_physician: medicalHistory?.under_care_of_physician ?? null,
          conditions_treated: medicalHistory?.conditions_treated ?? null,
          taking_medication: medicalHistory?.taking_medication ?? null,
          medications: medicalHistory?.medications ?? null,
          allergic_to_medications: medicalHistory?.allergic_to_medications ?? null,
          medications_allergies: medicalHistory?.medications_allergies ?? null,
          using_substances: medicalHistory?.using_substances ?? [],
          interested_in_stopping: medicalHistory?.interested_in_stopping ?? null,
          height: medicalHistory?.height ?? '',
          weight: medicalHistory?.weight ?? '',
          height_cm: medicalHistory?.height_cm ?? null,
          weight_kg: medicalHistory?.weight_kg ?? null,
          height_unit: medicalHistory?.height_unit,
          weight_unit: medicalHistory?.weight_unit,
          ...newPharmacy,
        }, apiRole);
      }
      await getFormAnswers(targetUserId, apiRole);
      setAddPharmacy(false);
      setFindPharmacy(false);
      showSnackbar(`You’ve successfully added ${newPharmacy.pharmacy_name} to your saved pharmacies`, 'success');
    } catch (error) {
      showSnackbar(`Error: ${error}`, 'error');
    }
  };

  const removePharmacy = async (): Promise<void> => {
    if (objectId) {
      await updateClinicalData('medical_history', objectId, {
        under_care_of_physician: medicalHistory?.under_care_of_physician ?? null,
        conditions_treated: medicalHistory?.conditions_treated ?? null,
        taking_medication: medicalHistory?.taking_medication ?? null,
        medications: medicalHistory?.medications ?? null,
        allergic_to_medications: medicalHistory?.allergic_to_medications ?? null,
        medications_allergies: medicalHistory?.medications_allergies ?? null,
        using_substances: medicalHistory?.using_substances ?? [],
        interested_in_stopping: medicalHistory?.interested_in_stopping ?? null,
        height: medicalHistory?.height ?? '',
        weight: medicalHistory?.weight ?? '',
        height_cm: medicalHistory?.height_cm ?? null,
        weight_kg: medicalHistory?.weight_kg ?? null,
        height_unit: medicalHistory?.height_unit,
        weight_unit: medicalHistory?.weight_unit,
        pharmacy_name: '',
        pharmacy_street_address: '',
        pharmacy_city: '',
        pharmacy_state: '',
        pharmacy_zip_code: '',
        pharmacy_phone_number: '',
      }, apiRole);
      await getFormAnswers(targetUserId, apiRole);
      setRemoveItemModal(false);
    }
  };

  const getFormAnswers = useCallback(async (createdByPatientId: number | undefined, role?: string) => {
    setRender(false);
    const { medical_history } = await listClinicalData(createdByPatientId ?? 0, role);
    setMedicalHistory(medical_history ?? null);
    if (medical_history) {
      setObjectId(medical_history.id.toString());
    }

    setPharmacyName(medical_history?.pharmacy_name || '');

    setPharmacyAddress(
      medical_history?.pharmacy_street_address && medical_history?.pharmacy_city
        ? `${medical_history.pharmacy_street_address}\n${medical_history.pharmacy_city}, ${medical_history.pharmacy_state} ${medical_history.pharmacy_zip_code}`
        : '',
    );
    setRender(true);
  }, []);

  useEffect(() => {
    (async () => {
      setRender(false);
      setPharmacyAddress('');
      await getFormAnswers(targetUserId, apiRole);
      setRender(true);
    })();
  }, [userInfo, targetUserId, apiRole]);

  if (findPharmacy) {
    return (
      <Container
        isVisibleOnMobile
        isSidenavVisible={false}
        isIntakeProcess
        containerStyle={{
          justifyContent: screenSize.width >= 564 ? 'center' : 'start',
        }}
        childrenStyle={{
          zIndex: '2',
          paddingTop: screenSize.width > 768 ? '40px' : '16px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          alignSelf: 'center',
        }}
      >
        <div className={styles.pharmacyContainer}>
          <PatientIntakeNavigation progress={60} />
          <SearchForPharmacy
            setFindPharmacy={setFindPharmacy}
            setAddPharmacy={setAddPharmacy}
            AddNewPharmacy={AddNewPharmacy}
            isFromPatientIntake
          />
          <div className={styles.findPharmacyFooter}>
            <Button
              variant="text"
              className={styles.findPharmacyFooterBack}
              onClick={() => setFindPharmacy(false)}
              data-testid="confirm-pharmacy-back"
            >
              <ArrowBackIosNewIcon aria-hidden="true" />
              Back
            </Button>
          </div>
        </div>
      </Container>
    );
  }

  if (addPharmacy) {
    return (
      <Container
        isVisibleOnMobile
        isSidenavVisible={false}
        isIntakeProcess
        containerStyle={{
          justifyContent: screenSize.width >= 564 ? 'center' : 'start',
        }}
        childrenStyle={{
          zIndex: '2',
          paddingTop: screenSize.width > 768 ? '40px' : '16px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          alignSelf: 'center',
        }}
      >
        <div className={styles.pharmacyContainer}>
          <PatientIntakeNavigation progress={60} />
          <AddPharmacy
            setFindPharmacy={setFindPharmacy}
            setAddPharmacy={setAddPharmacy}
            AddNewPharmacy={AddNewPharmacy}
            isFromPatientIntake
          />
          <div className={styles.findPharmacyFooter}>
            <Button
              variant="text"
              className={styles.findPharmacyFooterBack}
              onClick={() => {
                setAddPharmacy(false);
                setFindPharmacy(true);
              }}
              data-testid="confirm-pharmacy-back"
            >
              <ArrowBackIosNewIcon aria-hidden="true" />
              Back
            </Button>
          </div>
        </div>
      </Container>
    );
  }

  if (render && !findPharmacy) {
    return (
      <Container
        isVisibleOnMobile
        isSidenavVisible={false}
        isIntakeProcess
        containerStyle={{
          justifyContent: screenSize.width >= 564 ? 'center' : 'start',
        }}
        childrenStyle={{
          zIndex: '2',
          paddingTop: screenSize.width > 768 ? '40px' : '16px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          alignSelf: 'center',
        }}
      >
        <div className={styles.pharmacyContainer}>
          <PatientIntakeNavigation progress={60} />
          <h2 className="fs-unmask" style={{ marginLeft: '16px' }}>My pharmacy</h2>
          {pharmacyName && pharmacyAddress ? (
            <div className={`fs-exclude  ${styles.pharmacyInfo}`} data-testid="saved-pharmacy-div">
              <div className={`fs-exclude  ${styles.pharmacyInfoLeft}`}>
                <span className={styles.sectionContentRowDetailsName}>{pharmacyName}</span>
                <span className={styles.sectionContentRowDetailsAddress} style={{ whiteSpace: 'pre-line' }}>
                  {pharmacyAddress}
                </span>
              </div>
              <div className={`fs-exclude  ${styles.pharmacyInfoRight}`}>
                <Button
                  variant="text"
                  color="error"
                  onClick={() => setRemoveItemModal(true)}
                  data-testid="saved-pharmacy-remove"
                >
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <div className={`fs-unmask  ${styles.noPharmacy}`}>
              <span>You haven’t added any pharmacies yet</span>
              <Button
                variant="contained"
                color="primary"
                data-testid="pharmacy-add-pharmacy"
                onClick={() => setFindPharmacy(true)}
              >
                Add A Pharmacy
              </Button>
            </div>
          )}
          {removeItemModal && (
            <RemoveItemModal
              isOpen={removeItemModal}
              toggleModal={setRemoveItemModal}
              itemName={`${pharmacyName}`}
              removeItem={removePharmacy}
            />
          )}
          <GenericSnackbar
            open={snackbar.isOpen}
            message={snackbar.message}
            type={snackbar.type}
            onClose={closeSnackbar}
          />
          <div className={styles.findPharmacyFooter}>
            <Button
              variant="text"
              className={styles.findPharmacyFooterBack}
              onClick={() => navigate(-1)}
              data-testid="confirm-pharmacy-back"
            >
              <ArrowBackIosNewIcon aria-hidden="true" />
              Back
            </Button>
            {pharmacyName && pharmacyAddress && (
              <Button
                variant="contained"
                className={styles.findPharmacyFooterNext}
                type="button"
                data-testid="confirm-pharmacy-next"
                onClick={() => {
                  if (
                    (values.find((item) => item.form_question_id === CASE_TYPE_QUESTION_ID)?.value.caseType as string) ===
                    CaseType.second_opinion
                  ) {
                    roleNavigate('/upload-image');
                  } else {
                    roleNavigate('/describe-pain-level');
                  }
                }}
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </Container>
    );
  }

  return <Loading fullScreen />;
};

export default SelectPharmacy;
